import { createReducer } from "typesafe-actions";
import { ECNR_BASE_RANGE_TYPE, IReportStore } from "./@types";
import reportActions, { TReportActions } from "./actions";
import withJobs from "redux/_core/job/reducers";
import reportConstants, { PAGE_SIZE, quarterLabels } from "./constants";
import moment from "moment";
import { ESIGNAL_REPORT_TYPE_VAL } from "../vessel/onboard/types";

const defaultState: IReportStore = {
	_jobs: {},
	garbage: {
		rawGarbageData: [],
		vesselId: "",
		year: moment().year().toString(),
		allVesselsGarbageDischargeData: [],
		allVesselsGarbageCategoryData: [],
		garbageCustomSearchData: [],
	},
	bilgeWater: {
		quarterLabels: [...quarterLabels],
		rawBilgeWaterData: [],
		vesselId: "",
		year: moment().year().toString(),
		allVesselBilgeWaterData: [],
		customSearchData: [],
	},
	ors: {
		vesselId: "",
		year: moment().year().toString(),
		rawOrsData: [],
		rawOrsVesselSpecificData: [],
		customSearchData: [],
	},
	cargoSlop: {
		rawCargoSlopData: [],
		rawCargoSlopVesselSpecificData: [],
		customSearchData: [],
	},
	oilSpillsInWater: {
		rawOilSpillsInWaterData: [],
		rawOilSpillsInWaterVesselSpecificData: [],
	},
	comprehensiveNoonReport: {
		rawComprehensiveNoonReportData: [],
		rawComprehensiveNoonReportVesselSpecificData: [],
	},
	cnrRanges: {
		rawCNRRangeVesselsData: [],
		columns: [],
		vesselSubType: undefined,
		vesselsSelected: [],
		reportType: ESIGNAL_REPORT_TYPE_VAL.STEAMING,
		baseRangeType: ECNR_BASE_RANGE_TYPE.LOCAL,
		config: undefined,
		rangeInfo: undefined,
		isEditModalVisible: false,
		pagination: {
			hasMore: true,
			limit: PAGE_SIZE,
			skip: 0,
			total: 0,
		},
	},
	navnuc: {
		rawNavnucData: [],
	},
	// Environmental data
	packaging: {
		vesselSpecificAggregateData: [],
		allVesselAggregateData: [],
		customSearchData: [],
	},
	emission: {
		vesselSpecificAggregateData: [],
		allVesselAggregateData: [],
		customSearchData: [],
	},
	refrigerant: {
		vesselSpecificAggregateData: [],
		allVesselAggregateData: [],
		customSearchData: [],
	},
	cleaningChemicals: {
		vesselSpecificAggregateData: [],
		allVesselAggregateData: [],
		customSearchData: [],
	},
	emfFleetUsageData: [],
	biofouling1FleetData: [],
	biofouling2FleetData: [],
	vesselId: "",
	year: moment().year().toString(),
	vesselType: "",
	vesselClass: "",
	filters: [],
	pagination: {
		hasMore: false,
		limit: 0,
		skip: 0,
		total: 0,
	},
};

const reportReducer = createReducer<IReportStore, TReportActions>(defaultState)
	.handleAction(
		reportActions.document.garbageRawDataSet,
		function garbageRawDataSet(state, action) {
			const { garbageRawData } = action.payload;
			return {
				...state,
				garbage: {
					...state.garbage,
					rawGarbageData: garbageRawData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.garbageCustomSearchDataSet,
		function garbageCustomSearchDataSet(state, action) {
			const { garbageCustomSearchData } = action.payload;
			return {
				...state,
				garbage: {
					...state.garbage,
					garbageCustomSearchData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.reportVesselIdSet,
		function reportVesselIdSet(state, action) {
			const { vesselId } = action.payload;
			return {
				...state,
				vesselId,
			};
		},
	)
	.handleAction(
		reportActions.document.reportVesselTypeSet,
		function reportVesselTypeSet(state, action) {
			const { vesselType } = action.payload;
			return {
				...state,
				vesselType,
			};
		},
	)
	.handleAction(
		reportActions.document.reportVesselClassSet,
		function reportVesselClassSet(state, action) {
			const { vesselClass } = action.payload;
			return {
				...state,
				vesselClass,
			};
		},
	)
	.handleAction(
		reportActions.document.reportYearSet,
		function reportYearSet(state, action) {
			const { year } = action.payload;
			return {
				...state,
				year,
			};
		},
	)
	.handleAction(
		reportActions.document.reportFiltersSet,
		function reportFiltersSet(state, action) {
			const { filters } = action.payload;
			return {
				...state,
				filters,
			};
		},
	)
	.handleAction(
		reportActions.document.reportPaginationSet,
		function reportPaginationSet(state, action) {
			const { pagination } = action.payload;
			return {
				...state,
				pagination,
			};
		},
	)
	.handleAction(
		reportActions.document.garbageReportVesselIdSet,
		function garbageReportVesselIdSet(state, action) {
			const { vesselId } = action.payload;
			return {
				...state,
				garbage: {
					...state.garbage,
					vesselId,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.garbageReportYearSet,
		function garbageReportYearSet(state, action) {
			const { year } = action.payload;
			return {
				...state,
				garbage: {
					...state.garbage,
					year,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.bilgeWaterCustomSearchDataSet,
		function bilgeWaterCustomSearchDataSet(state, action) {
			const { bilgeWaterCustomSearchData } = action.payload;
			return {
				...state,
				bilgeWater: {
					...state.bilgeWater,
					customSearchData: bilgeWaterCustomSearchData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.bilgeWaterRawDataSet,
		function bilgeWaterRawDataSet(state, action) {
			const { bilgeWaterRawData } = action.payload;
			return {
				...state,
				bilgeWater: {
					...state.bilgeWater,
					rawBilgeWaterData: bilgeWaterRawData ?? null,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.bilgeWaterReportVesselIdSet,
		function bilgeWaterReportVesselIdSet(state, action) {
			const { vesselId } = action.payload;
			return {
				...state,
				bilgeWater: {
					...state.bilgeWater,
					vesselId,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.bilgeWaterReportYearSet,
		function bilgeWaterReportYearSet(state, action) {
			const { year } = action.payload;
			return {
				...state,
				bilgeWater: {
					...state.bilgeWater,
					year,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.bilgeWaterAllVesselDataSet,
		function bilgeWaterAllVesselDataSet(state, action) {
			const { vesselBilgeWaterRecords } = action.payload;
			return {
				...state,
				bilgeWater: {
					...state.bilgeWater,
					allVesselBilgeWaterData: vesselBilgeWaterRecords,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.garbageDischargeAllVesselDataSet,
		function garbageDischargeAllVesselDataSet(state, action) {
			const { garbageDischargeRecords } = action.payload;
			return {
				...state,
				garbage: {
					...state.garbage,
					allVesselsGarbageDischargeData: garbageDischargeRecords,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.garbageCategoryAllVesselDataSet,
		function garbageCategoryAllVesselDataSet(state, action) {
			const { garbageCategoryRecords } = action.payload;
			return {
				...state,
				garbage: {
					...state.garbage,
					allVesselsGarbageCategoryData: garbageCategoryRecords,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.orsReportVesselIdSet,
		function orsReportVesselIdSet(state, action) {
			const { vesselId } = action.payload;
			return {
				...state,
				ors: {
					...state.ors,
					vesselId,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.orsReportYearSet,
		function orsReportYearSet(state, action) {
			const { year } = action.payload;
			return {
				...state,
				ors: {
					...state.ors,
					year,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.orsRawDataSet,
		function orsRawDataSet(state, action) {
			const { rawOrsData } = action.payload;
			return {
				...state,
				ors: {
					...state.ors,
					rawOrsData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.orsVesselSpecificRawDataSet,
		function orsVesselSpecificRawDataSet(state, action) {
			const { rawVesselSpecificOrsData } = action.payload;
			return {
				...state,
				ors: {
					...state.ors,
					rawOrsVesselSpecificData: rawVesselSpecificOrsData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cargoSlopRawDataSet,
		function cargoSlopRawDataSet(state, action) {
			const { rawCargoSlopData } = action.payload;
			return {
				...state,
				cargoSlop: {
					...state.cargoSlop,
					rawCargoSlopData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cargoSlopVesselSpecificRawDataSet,
		function cargoSlopVesselSpecificRawDataSet(state, action) {
			const { rawVesselSpecificCargoSlopData } = action.payload;
			return {
				...state,
				cargoSlop: {
					...state.cargoSlop,
					rawCargoSlopVesselSpecificData: rawVesselSpecificCargoSlopData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.oilSpillsInWaterRawDataSet,
		function oilSpillsInWaterRawDataSet(state, action) {
			const { rawOilSpillsInWaterData } = action.payload;
			return {
				...state,
				oilSpillsInWater: {
					...state.oilSpillsInWater,
					rawOilSpillsInWaterData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.oilSpillsInWaterVesselSpecificRawDataSet,
		function oilSpillsInWaterVesselSpecificRawDataSet(state, action) {
			const { rawVesselSpecificOilSpillsInWaterData } = action.payload;
			return {
				...state,
				oilSpillsInWater: {
					...state.oilSpillsInWater,
					rawOilSpillsInWaterVesselSpecificData:
						rawVesselSpecificOilSpillsInWaterData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.comprehensiveNoonReportRawDataSet,
		function comprehensiveNoonReportRawDataSet(state, action) {
			const { rawComprehensiveNoonReportData } = action.payload;
			return {
				...state,
				comprehensiveNoonReport: {
					...state.comprehensiveNoonReport,
					rawComprehensiveNoonReportData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.comprehensiveNoonReportVesselSpecificDataSet,
		function comprehensiveNoonReportVesselSpecificDataSet(state, action) {
			const { rawVesselSpefiicDataComprehensiveNoonReportData } =
				action.payload;
			return {
				...state,
				comprehensiveNoonReport: {
					...state.comprehensiveNoonReport,
					rawComprehensiveNoonReportVesselSpecificData:
						rawVesselSpefiicDataComprehensiveNoonReportData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cnrRangesVesselListSet,
		function cnrRangesVesselListSet(state, action) {
			const { vesselList, type } = action.payload;
			return {
				...state,
				cnrRanges: {
					...state.cnrRanges,
					rawCNRRangeVesselsData:
						type === "add"
							? [...state.cnrRanges.rawCNRRangeVesselsData, ...vesselList]
							: [...vesselList],
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cnrRangesVesselListReset,
		function cnrRangesVesselListReset(state, action) {
			return {
				...state,
				cnrRanges: {
					...state.cnrRanges,
					rawCNRRangeVesselsData: [],
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cnrRangesPaginationSet,
		function cnrRangesPaginationSet(state, action) {
			const { key } = action.payload;
			return {
				...state,
				cnrRanges: {
					...state.cnrRanges,
					pagination: {
						...state.cnrRanges.pagination,
						...key,
					},
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cnrRangesColumnsSet,
		function cnrRangesColumnsSet(state, action) {
			const { columns } = action.payload;
			return {
				...state,
				cnrRanges: {
					...state.cnrRanges,
					columns,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cnrRangesVesselSubTypeSet,
		function cnrRangesVesselSubTypeSet(state, action) {
			const { vesselSubType } = action.payload;
			return {
				...state,
				cnrRanges: {
					...state.cnrRanges,
					vesselSubType,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cnrRangesVesselsSelected,
		function cnrRangesVesselsSelected(state, action) {
			const { vesselsSelected = [] } = action.payload;
			return {
				...state,
				cnrRanges: {
					...state.cnrRanges,
					vesselsSelected,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cnrRangesReportTypeSelected,
		function cnrRangesReportTypeSelected(state, action) {
			const { reportType } = action.payload;
			return {
				...state,
				cnrRanges: {
					...state.cnrRanges,
					reportType,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cnrRangesBaseRangeTypeSet,
		function cnrRangesBaseRangeTypeSet(state, action) {
			const { baseRangeType } = action.payload;
			return {
				...state,
				cnrRanges: {
					...state.cnrRanges,
					baseRangeType,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cnrRangesConfigSet,
		function cnrRangesConfigSet(state, action) {
			const { config } = action.payload;
			return {
				...state,
				cnrRanges: {
					...state.cnrRanges,
					config,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cnrRangesRangeInfoSet,
		function cnrRangesRangeInfoSet(state, action) {
			const { rangeInfo } = action.payload;
			return {
				...state,
				cnrRanges: {
					...state.cnrRanges,
					rangeInfo,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cnrRangesIsEditModalVisibleToggle,
		function cnrRangesIsEditModalVisibleToggle(state, action) {
			const { isEditModalVisible } = action.payload;
			return {
				...state,
				cnrRanges: {
					...state.cnrRanges,
					isEditModalVisible:
						typeof isEditModalVisible === "undefined"
							? !state.cnrRanges.isEditModalVisible
							: isEditModalVisible,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.navnucRawDataSet,
		function navnucRawDataSet(state, action) {
			const { rawNavnucData } = action.payload;
			return {
				...state,
				navnuc: {
					...state.navnuc,
					rawNavnucData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cleaningChemicalsAllVesselAggregateDataSet,
		function cleaningChemicalsAllVesselAggregateDataSet(state, action) {
			const { cleaningChemicalsAllVesselAggregateData } = action.payload;
			return {
				...state,
				cleaningChemicals: {
					...state.cleaningChemicals,
					allVesselAggregateData: cleaningChemicalsAllVesselAggregateData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cleaningChemicalsVesselSpecificAggregateDataSet,
		function cleaningChemicalsVesselSpecificAggregateDataSet(state, action) {
			const { cleaningChemicalsVesselSpecificAggregateData } = action.payload;
			return {
				...state,
				cleaningChemicals: {
					...state.cleaningChemicals,
					vesselSpecificAggregateData:
						cleaningChemicalsVesselSpecificAggregateData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.packagingCustomSearchDataSet,
		function packagingCustomSearchDataSet(state, action) {
			const { packagingCustomSearchData } = action.payload;
			return {
				...state,
				packaging: {
					...state.packaging,
					customSearchData: packagingCustomSearchData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cleaningChemicalsCustomSearchDataSet,
		function cleaningChemicalsCustomSearchDataSet(state, action) {
			const { cleaningChemicalsCustomSearchData } = action.payload;
			return {
				...state,
				cleaningChemicals: {
					...state.cleaningChemicals,
					customSearchData: cleaningChemicalsCustomSearchData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.orsCustomSearchDataSet,
		function orsCustomSearchDataSet(state, action) {
			const { orsCustomSearchData } = action.payload;
			return {
				...state,
				ors: {
					...state.ors,
					customSearchData: orsCustomSearchData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.cargoSlopCustomSearchDataSet,
		function cargoSlopCustomSearchDataSet(state, action) {
			const { cargoSlopCustomSearchData } = action.payload;
			return {
				...state,
				cargoSlop: {
					...state.cargoSlop,
					customSearchData: cargoSlopCustomSearchData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.emissionsCustomSearchDataSet,
		function emissionsCustomSearchDataSet(state, action) {
			const { emissionsCustomSearchData } = action.payload;
			return {
				...state,
				emission: {
					...state.emission,
					customSearchData: emissionsCustomSearchData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.packagingAllVesselAggregateDataSet,
		function packagingAllVesselAggregateDataSet(state, action) {
			const { packagingAllVesselAggregateData } = action.payload;
			return {
				...state,
				packaging: {
					...state.packaging,
					allVesselAggregateData: packagingAllVesselAggregateData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.packagingVesselSpecificAggregateDataSet,
		function packagingVesselSpecificAggregateDataSet(state, action) {
			const { packagingVesselSpecificAggregateData } = action.payload;
			return {
				...state,
				packaging: {
					...state.packaging,
					vesselSpecificAggregateData: packagingVesselSpecificAggregateData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.emissionsAllVesselAggregateDataSet,
		function emissionsAllVesselAggregateDataSet(state, action) {
			const { emissionsAllVesselAggregateData } = action.payload;
			return {
				...state,
				emission: {
					...state.emission,
					allVesselAggregateData: emissionsAllVesselAggregateData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.emissionsVesselSpecificAggregateDataSet,
		function emissionsVesselSpecificAggregateDataSet(state, action) {
			const { emissionsVesselSpecificAggregateData } = action.payload;
			return {
				...state,
				emission: {
					...state.emission,
					vesselSpecificAggregateData: emissionsVesselSpecificAggregateData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.refrigerantCustomSearchDataSet,
		function refrigerantCustomSearchDataSet(state, action) {
			const { refrigerantCustomSearchData } = action.payload;
			return {
				...state,
				refrigerant: {
					...state.refrigerant,
					customSearchData: refrigerantCustomSearchData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.refrigerantAllVesselAggregateDataSet,
		function refrigerantAllVesselAggregateDataSet(state, action) {
			const { refrigerantAllVesselAggregateData } = action.payload;
			return {
				...state,
				refrigerant: {
					...state.refrigerant,
					allVesselAggregateData: refrigerantAllVesselAggregateData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.refrigerantVesselSpecificAggregateDataSet,
		function refrigerantVesselSpecificAggregateDataSet(state, action) {
			const { refrigerantVesselSpecificAggregateData } = action.payload;
			return {
				...state,
				refrigerant: {
					...state.refrigerant,
					vesselSpecificAggregateData: refrigerantVesselSpecificAggregateData,
				},
			};
		},
	)
	.handleAction(
		reportActions.document.emfFleetUsageDataSet,
		function emfFleetUsageDataSet(state, action) {
			const { emfUsageDataSet: emfFleetUsageData } = action.payload;
			return {
				...state,
				emfFleetUsageData,
			};
		},
	)
	.handleAction(
		reportActions.document.biofouling1FleetDataSet,
		function biofouling1FleetDataSet(state, action) {
			return {
				...state,
				biofouling1FleetData: action.payload.biofouling1Data,
			};
		},
	)
	.handleAction(
		reportActions.document.biofouling2FleetDataSet,
		function biofouling2FleetDataSet(state, action) {
			return {
				...state,
				biofouling2FleetData: action.payload.biofouling2Data,
			};
		},
	);

export default withJobs<IReportStore>(
	reportConstants.FEATURE,
	defaultState,
	reportReducer,
);
